/**
 * Les ensembles fermés du monde.
 *
 * Ils sont fermés par le lore, pas par commodité de production : il y a huit
 * classes et huit artefacts, et « il n'y aura pas de neuvième artefact ultime ».
 * On les encode donc comme des ensembles finis, ici et en base.
 */
/** Les huit classes. Canon, `01-SYSTEMES.md`. */
export const CLASSES = [
    'guerrier',
    'gardien',
    'rogue',
    'eclaireur',
    'mage',
    'oracle',
    'invocateur',
    'heraut',
    /**
     * DIEU — la classe d'administration, hors du monde.
     *
     * Elle n'est proposée à personne : `optionsCreation` la retire de la liste,
     * et aucun contenu ne la nomme. Elle existe pour qu'un personnage
     * d'administration puisse porter ce qu'on lui met sans qu'on ait à tordre une
     * classe jouable pour ça.
     *
     * Elle est dernière dans la liste, ce qui n'est pas indifférent : les huit
     * premières se répondent une à une avec les huit artefacts, et cette
     * correspondance ne doit pas être décalée par une neuvième.
     */
    'dieu',
];
/** Fantaisie centrale et artefact de chaque classe. Canon, `01-SYSTEMES.md`. */
export const CLASSE_INFO = {
    guerrier: { nom: 'Guerrier', role: 'Mêlée lourde', fantaisie: 'Tenir la ligne', artefact: 'perennis' },
    gardien: { nom: 'Gardien', role: 'Tank', fantaisie: 'Être regardé, encaisser pour les autres', artefact: 'pharos' },
    rogue: { nom: 'Rogue', role: 'Mêlée furtive', fantaisie: 'Ne pas être vu', artefact: 'novacula' },
    eclaireur: { nom: 'Éclaireur', role: 'Distance physique', fantaisie: 'Pister, tenir à distance', artefact: 'argus' },
    mage: { nom: 'Mage', role: 'Distance magique', fantaisie: 'Plier les lois', artefact: 'axioma' },
    oracle: { nom: 'Oracle', role: 'Soins', fantaisie: 'Réparer, dissiper, prévoir', artefact: 'nexum' },
    invocateur: { nom: 'Invocateur', role: 'Entités', fantaisie: 'Ne jamais être seul', artefact: 'ultimus' },
    dieu: { nom: 'Dieu', role: 'Hors du monde', fantaisie: 'Tenir ce qui ne se donne pas', artefact: 'aucun' },
    heraut: { nom: 'Héraut', role: 'Soutien', fantaisie: 'Amplifier les autres', artefact: 'fiat' },
};
/**
 * Les six origines.
 *
 * Toutes tirées du mobilier du monde (relevés, registres, routes, carrières),
 * jamais d'un cliché de fantasy. « Sans registre » est la seule qui se définisse
 * par une absence, ce qui est la forme du monde.
 */
export const ORIGINES = [
    'relais_de_cartographes',
    'carrieres',
    'maison_de_comptes',
    'suite_de_convoi',
    'hospice',
    'sans_registre',
];
/**
 * Le nom lisible d'une origine, écrit UNE fois.
 *
 * Il vivait en double, dans `server/domaine/origines.ts` et dans l'écran de
 * fiche, et les deux ont divergé à la première renomination : le serveur
 * disait « La Descente » quand le client disait encore « Carrières ». Un
 * libellé dupliqué n'est pas une redondance inoffensive, c'est un endroit où
 * l'on oubliera.
 *
 * Une origine ne dit plus où l'on pointait mais **ce que le monde vous a appris
 * avant que vous partiez**. C'est la même correction que sur les factions : on
 * ne nomme pas les gens par leur emploi.
 */
export const NOMS_ORIGINES = {
    relais_de_cartographes: 'Le Faux Pied',
    carrieres: 'La Descente',
    maison_de_comptes: 'La Colonne de droite',
    suite_de_convoi: 'Le Bas-Côté',
    hospice: 'La Veille',
    sans_registre: 'Sans registre',
};
/** Les cinq statistiques. `04-JALON-1-SOLO.md` §4. */
export const STATS = ['force', 'adresse', 'esprit', 'vigueur', 'fortune'];
/** Axe A : rareté, huit paliers. Canon, `01-SYSTEMES.md`. */
export const PALIERS = [
    'standard',
    'commun',
    'fin',
    'rare',
    'epique',
    'legendaire',
    'mythique',
    'ultime',
];
/**
 * Couleur de palier. Canon, `01-SYSTEMES.md`.
 * Le palier 07 n'a pas de couleur fixe : il est irisé et animé, et c'est le seul
 * indice visuel que le jeu donne sans jamais l'expliquer.
 */
export const PALIER_COULEUR = {
    standard: '#6B6E75',
    commun: '#EDEFF2',
    fin: '#43BE68',
    rare: '#3C81E3',
    epique: '#A335EE',
    legendaire: '#FF8A16',
    mythique: '#B0182C',
    ultime: null,
};
/** Axe B : singularité. Canon, `01-SYSTEMES.md`. Distinct de la rareté. */
export const SINGULARITES = ['repandu', 'contingente', 'atteste', 'un_seul'];
/** Emplacements d'équipement ordinaires. Les artefacts ont le leur, qui n'existe que pour eux. */
export const EMPLACEMENTS = [
    'tete',
    'cou',
    'torse',
    'dos',
    'mains',
    'taille',
    'jambes',
    'pieds',
    'arme_principale',
    'arme_secondaire',
    'anneau_1',
    'anneau_2',
];
/**
 * Les libellés qu'un joueur lit, contre les codes qu'on écrit.
 *
 * Enzo, en jouant : « j'ai une canne à chaîner. Je ne sais pas ce que c'est, je
 * ne sais pas si c'est pour moi, je ne sais même pas où ça se met. Quand je
 * clique, il ne se passe rien. Il manque beaucoup d'informations sur les
 * objets. »
 *
 * Il avait raison sur les quatre points, et sur le dernier littéralement : la
 * Canne à chaîner est un `baton`, un rogue ne tient pas de bâton, donc le bouton
 * ne pouvait rien faire. Rien ne le lui disait.
 *
 * Ces tables vivent ici, dans `shared/`, et pas dans l'écran qui les affiche :
 * l'inventaire avait déjà les siennes pour les emplacements, l'échoppe, le
 * marché et la poupée n'en avaient aucune. Trois écrans nommaient la même chose
 * de trois façons, ou pas du tout.
 */
export const NOM_PALIER = {
    standard: 'Standard',
    commun: 'Commun',
    fin: 'Fin',
    rare: 'Rare',
    epique: 'Épique',
    legendaire: 'Légendaire',
    mythique: 'Mythique',
    ultime: 'Ultime',
};
export const NOM_EMPLACEMENT = {
    tete: 'Tête',
    cou: 'Cou',
    torse: 'Torse',
    dos: 'Dos',
    mains: 'Mains',
    taille: 'Taille',
    jambes: 'Jambes',
    pieds: 'Pieds',
    arme_principale: 'Arme',
    arme_secondaire: 'Main gauche (moitié)',
    anneau_1: 'Anneau',
    anneau_2: 'Anneau',
};
/**
 * Les familles d'objet.
 *
 * Ce sont les silhouettes dessinées à la main du générateur d'icônes.
 * Une icône vaut `cadre(palier) + silhouette(famille) + teinte(materiau)` :
 * le cadre dit le palier, l'illustration dit l'objet. Canon, `01-SYSTEMES.md`.
 */
export const FAMILLES = [
    'epee',
    'dague',
    'hache',
    'masse',
    'baton',
    'arc',
    'arbalete',
    'bouclier',
    'heaume',
    'plastron',
    'harnais',
    'gantelets',
    'ceinture',
    'jambieres',
    'bottes',
    'anneau',
    'amulette',
];
/** Ce que la pièce EST, dit en un mot. « Canne à chaîner » ne dit pas « bâton ». */
export const NOM_FAMILLE = {
    epee: 'Épée',
    dague: 'Dague',
    hache: 'Hache',
    masse: 'Masse',
    baton: 'Bâton',
    arc: 'Arc',
    arbalete: 'Arbalète',
    bouclier: 'Bouclier',
    heaume: 'Heaume',
    plastron: 'Plastron',
    harnais: 'Harnais',
    gantelets: 'Gantelets',
    ceinture: 'Ceinture',
    jambieres: 'Jambières',
    bottes: 'Bottes',
    anneau: 'Anneau',
    amulette: 'Amulette',
};
/**
 * L'article défini de chaque famille, élision comprise.
 *
 * Sans lui, la phrase qui explique un refus s'écrit « un rogue ne manie pas la
 * bâton ». Le genre d'un nom n'est pas devinable depuis son code, et une table
 * de dix-sept entrées coûte moins cher qu'une tournure contournée à chaque fois.
 */
export const ARTICLE_FAMILLE = {
    epee: 'l’', dague: 'la ', hache: 'la ', masse: 'la ', baton: 'le ',
    arc: 'l’', arbalete: 'l’', bouclier: 'le ', heaume: 'le ', plastron: 'le ',
    harnais: 'le ', gantelets: 'les ', ceinture: 'la ', jambieres: 'les ',
    bottes: 'les ', anneau: 'l’', amulette: 'l’',
};
/** Les huit artefacts. Fermé par le lore. Aucun n'est implémenté au jalon 1. */
export const ARTEFACTS = [
    'novacula',
    'pharos',
    'nexum',
    'perennis',
    'argus',
    'axioma',
    'ultimus',
    'fiat',
];
/**
 * Les trois approches.
 *
 * C'est là que se joue l'agence du joueur quand il n'est pas là : l'approche
 * qu'il pose avant l'engagement devient la consigne permanente du pilote
 * automatique. Quand il est là, elle module encore ce qu'il fait.
 */
export const APPROCHES = ['prudente', 'directe', 'rusee'];
export const ISSUES = ['victoire', 'defaite', 'fuite'];
/**
 * L'unique projection d'un score caché vers le joueur. Canon, `01-SYSTEMES.md` et
 * `artefacts-ultimes.md` 0.8 : cinq bandes, et le porteur sait dans laquelle il est,
 * jamais où il en est dans la bande.
 *
 * Les frontières d'affichage n'ont aucune obligation de coïncider avec les seuils
 * mécaniques : le décalage est un levier de réglage à part entière.
 */
export const ETATS_VISIBLES = ['I', 'II', 'III', 'IV', 'V'];
/** Bandes par défaut. Un artefact peut décaler son affichage en avance ou en retard. */
export const BANDES_ETAT = [
    { etat: 'I', min: 0, max: 20 },
    { etat: 'II', min: 20, max: 45 },
    { etat: 'III', min: 45, max: 70 },
    { etat: 'IV', min: 70, max: 90 },
    { etat: 'V', min: 90, max: 100.01 },
];
/**
 * Quantifie un score caché en état visible.
 *
 * C'est la SEULE fonction autorisée à lire un score `0-100` pour produire quelque
 * chose de destiné au client. Elle vit dans `shared/` parce que les bandes sont
 * une donnée de conception, mais elle n'est appelée que côté serveur.
 */
export function etatVisible(score, decalage = 0) {
    const v = Math.min(100, Math.max(0, score + decalage));
    for (const b of BANDES_ETAT)
        if (v >= b.min && v < b.max)
            return b.etat;
    return 'V';
}
export const PRISE = {
    dague: 'une_main',
    hache: 'une_main',
    masse: 'une_main',
    bouclier: 'une_main',
    epee: 'deux_mains',
    arc: 'deux_mains',
    arbalete: 'deux_mains',
    baton: 'deux_mains',
};
/**
 * Ce que chaque classe accepte de tenir.
 *
 * ## Ce que le rogue tenait, et pourquoi c'était cassé
 *
 * Enzo, en jouant : « pourquoi je peux porter les masses en tant que rogue ?
 * C'est une arme de guerrier. La masse est un item désigné pour être
 * suffisamment puissant comme arme seule, car seul le rogue peut avoir deux
 * armes. Et moi je peux porter deux masses. Donc je pourrais avoir deux masses
 * et ne jamais jouer aux dagues, puisque les masses sont structurellement plus
 * fortes. »
 *
 * Mesuré sur le catalogue, à niveau égal, dégâts moyens :
 *
 *     niveau ~12   dague 17   masse 22   hache 27
 *     niveau ~19   dague 27   masse 36   hache 41
 *     niveau ~25   dague 41   masse 55   hache 61
 *
 * La masse fait **1,33 fois** la dague et la hache **1,5 fois**, à chaque
 * palier. Elles sont calibrées comme arme UNIQUE d'une classe qui n'a qu'une
 * main d'arme ; la dague est calibrée comme MOITIÉ DE PAIRE. Laisser le rogue
 * tenir une masse rendait donc la dague — son arme d'identité — strictement
 * dominée, à toutes les tailles.
 *
 * Le rogue garde ce qui se manie vite : la dague dans les deux mains, et
 * l'épée, l'arc ou l'arbalète qui prennent les deux et se jouent seuls.
 *
 * ## Les sept autres restent permissives, et c'est toujours assumé
 *
 * `CLAUDE.md` pose qu'une lacune qui ne pénalise qu'une autre classe n'est pas
 * une dette, et qu'on n'écrit pas le contenu des maisons qu'on ne joue pas.
 * Le jour où l'une d'elles se joue, sa ligne s'écrit ici et nulle part ailleurs.
 * Ce qui les empêche désormais d'abuser de la même faille n'est pas leur liste,
 * c'est `DEUX_ARMES` ci-dessous.
 */
const TOUTES_ARMES = ['dague', 'hache', 'masse', 'epee', 'arc', 'arbalete', 'baton'];
const AVEC_BOUCLIER = [...TOUTES_ARMES, 'bouclier'];
export const MAINS_PAR_CLASSE = {
    // Dieu tient tout, y compris ce qu'aucune classe ne tient. C'est une classe
    // d'administration : elle ne subit aucune des règles qui font le jeu.
    dieu: AVEC_BOUCLIER,
    // Ni masse ni hache : elles sont calibrées comme arme unique, et le rogue
    // est le seul à pouvoir en tenir deux. Voir le relevé de dégâts ci-dessus.
    rogue: ['dague', 'epee', 'arc', 'arbalete'],
    guerrier: AVEC_BOUCLIER,
    gardien: AVEC_BOUCLIER,
    eclaireur: AVEC_BOUCLIER,
    mage: AVEC_BOUCLIER,
    oracle: AVEC_BOUCLIER,
    invocateur: AVEC_BOUCLIER,
    heraut: AVEC_BOUCLIER,
};
/** Les deux emplacements de main. */
export const MAINS = ['arme_principale', 'arme_secondaire'];
/**
 * Les deux doigts, et pourquoi ils se traitent comme les deux mains.
 *
 * Enzo : « j'ai un anneau sur moi et un autre en inventaire. Or j'ai deux cases
 * pour porter des anneaux. Et quand je clique sur celui de l'inventaire pour
 * l'ajouter à mon slot libre, ça remplace le premier. Je devrais pouvoir
 * choisir. »
 *
 * `emplacementsPossibles` sortait sur `if (!MAINS.includes(...)) return
 * [emplacementModele]` : tout ce qui n'est pas une arme ne proposait qu'UN
 * emplacement, celui écrit sur le modèle. Les anneaux portent tous
 * `anneau_1`, donc **le second doigt était inatteignable** — la case existait
 * à l'écran et rien ne pouvait y aller.
 *
 * Une paire est une paire : ce qui vaut pour les mains vaut pour les doigts.
 */
export const DOIGTS = ['anneau_1', 'anneau_2'];
/**
 * Les classes qui peuvent tenir une seconde ARME. Les autres n'y mettent qu'un
 * bouclier.
 *
 * Enzo, en posant le problème : « seul le rogue peut avoir deux armes ». C'était
 * l'intention, ce n'était pas la règle : `emplacementsPossibles` rendait les
 * deux mains à **toute** classe pour **toute** famille à une main, si bien qu'un
 * guerrier pouvait tenir deux masses aussi bien qu'un rogue.
 *
 * Ça n'avait aucun effet visible tant que la seconde main ne servait à rien —
 * elle ne comptait que ses bonus. Ça en aurait un dès que la seconde arme se
 * met à frapper, ce qu'elle fait maintenant : sans cette liste, le double
 * maniement cesserait d'être la signature du rogue le jour même où il commence
 * à valoir quelque chose.
 *
 * `dieu` y figure parce qu'elle ne subit aucune des règles qui font le jeu.
 */
export const DEUX_ARMES = ['rogue', 'dieu'];
/**
 * Où cette pièce peut aller, pour cette classe. Vide si elle ne peut nulle part.
 *
 * Trois questions dans cet ordre, et c'est le seul juge du jeu : le serveur
 * comme le client l'appellent, personne ne réimplémente la règle à côté.
 *
 *   1. **Est-ce que ça se tient ?** Une cuirasse va à son emplacement, point.
 *   2. **Est-ce que cette classe manie cette famille ?** Sinon, aucune main.
 *   3. **Est-ce que la seconde main est ouverte ?** Une arme à deux mains la
 *      condamne ; un bouclier y va toujours ; une seconde arme, seulement pour
 *      les classes qui savent en tenir deux.
 */
export function emplacementsPossibles(famille, emplacementModele, classe) {
    // Un anneau va à l'un ou l'autre doigt, comme une arme à une main va à l'une
    // ou l'autre main. Aucune classe n'y met de condition : un doigt est un doigt.
    if (DOIGTS.includes(emplacementModele))
        return [...DOIGTS];
    if (!MAINS.includes(emplacementModele))
        return [emplacementModele];
    if (!MAINS_PAR_CLASSE[classe].includes(famille))
        return [];
    if (PRISE[famille] !== 'une_main')
        return ['arme_principale'];
    // Un bouclier n'est pas une seconde arme : il va dans la main gauche de
    // n'importe qui capable de le porter, et c'est justement ce qui distingue
    // tenir un bouclier de manier deux armes.
    const secondeOuverte = famille === 'bouclier' || DEUX_ARMES.includes(classe);
    return secondeOuverte ? [...MAINS] : ['arme_principale'];
}
export function blocage(piece, niveau) {
    if (piece.categorie === 'materiau')
        return null;
    /*
     * Deux sources pour la même question, et un ordre entre elles.
     *
     * La plupart des écrans reçoivent des pièces sans savoir à QUI elles sont
     * montrées : le butin d'un combat, un rayon de confrérie, un sac de donjon.
     * Leur faire porter le niveau du personnage jusque-là aurait voulu dire le
     * faire descendre par cinq écrans intermédiaires qui n'en ont aucun usage.
     * Le serveur, lui, connaît le personnage à coup sûr : il tranche, et son
     * verdict prime. Le niveau ne sert que pour les formes complètes qui ne le
     * portent pas, comme la pièce dans la poupée.
     *
     * Et quand on n'a ni l'un ni l'autre, on ne conclut pas. Peindre en rouge une
     * pièce parfaitement portable serait pire que ne rien peindre du tout.
     */
    const horsDePortee = piece.horsDePortee ?? (niveau === undefined ? false : piece.niveauRequis > niveau);
    if (horsDePortee)
        return 'niveau';
    if (piece.categorie === 'equipement' && piece.emplacementsPossibles?.length === 0) {
        return 'classe';
    }
    return null;
}
/**
 * Lequel des emplacements possibles la pièce occupera, faute de choix explicite.
 *
 * La règle est simple — le premier libre, sinon le premier tout court — mais
 * elle vivait **dans la route serveur uniquement**, et la poupée n'avait donc
 * aucun moyen d'annoncer où une pièce allait atterrir. Enzo : « quand je clique
 * sur un accessoire de mon inventaire, j'aimerais que ça encadre la case de la
 * poupée vers là où ça va aller, parce qu'on n'est pas toujours sûr. »
 *
 * La montrer sans la partager aurait voulu dire la réécrire côté client, donc
 * la décrire à deux endroits, donc la voir diverger le jour où elle change.
 * Elle est écrite ici, et les deux côtés l'appellent.
 *
 * `porte` est passé en fonction plutôt qu'en liste : le serveur interroge les
 * objets du personnage, le client une table par emplacement, et ni l'un ni
 * l'autre n'a à se convertir pour poser la question.
 */
export function emplacementRetenu(possibles, porte, deuxMainsPrises) {
    // La seconde main n'est pas « libre » quand la première tient à deux mains :
    // la donner par défaut ferait échouer la pose au lieu d'échanger l'arme.
    const libre = possibles.find((e) => !porte(e) && !(e === 'arme_secondaire' && deuxMainsPrises));
    return libre ?? possibles[0];
}
